import { useEffect, useState, useRef } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, X, Film, UploadCloud, CheckCircle2, Loader2 } from "lucide-react";

const EMPTY = {
  title: "", description: "", release_year: 2024, origin: "USA", quality: "HD",
  genre: "Drama", category: "Trending", poster_url: "", backdrop_url: "",
  stream_url: "", stream_type: "mp4", duration: "01:45:00", rating: 8.0, featured: false,
};

const inputCls = "w-full bg-[#111111] border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#00F0FF]";

const Field = ({ label, children }) => (
  <div>
    <label className="text-xs uppercase tracking-widest text-white/50">{label}</label>
    <div className="mt-1.5">{children}</div>
  </div>
);

// Uploader: file picker -> uploads to storage -> fills the target URL field
const Uploader = ({ label, kind, value, onUploaded, accept, testid }) => {
  const ref = useRef(null);
  const [progress, setProgress] = useState(0);
  const [busy, setBusy] = useState(false);

  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    setProgress(0);
    try {
      if (kind === "video") {
        // Chunked upload — each piece stays under the CDN body-size cap, backend reassembles in order.
        const chunkSize = 50 * 1024 * 1024;
        const totalChunks = Math.max(1, Math.ceil(file.size / chunkSize));
        const { data: init } = await api.post("/admin/upload/init", { filename: file.name, total_chunks: totalChunks }, { timeout: 0 });
        const uploadId = init.upload_id;
        const size = init.chunk_size || chunkSize;
        let uploaded = 0;
        let index = 0;
        for (let start = 0; start < file.size; start += size) {
          const blob = file.slice(start, Math.min(start + size, file.size));
          const fd = new FormData();
          fd.append("file", blob, file.name);
          const base = uploaded;
          const idx = index;
          await api.post(`/admin/upload/chunk?upload_id=${uploadId}&chunk_index=${idx}`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
            timeout: 0,
            onUploadProgress: (p) => {
              const done = base + (p.loaded || 0);
              setProgress(Math.min(99, Math.round((done / file.size) * 100)));
            },
          });
          uploaded += blob.size;
          index += 1;
          setProgress(Math.min(99, Math.round((uploaded / file.size) * 100)));
        }
        const { data } = await api.post("/admin/upload/finalize", { upload_id: uploadId }, { timeout: 0 });
        setProgress(100);
        onUploaded(data);
        toast.success(`${label} uploaded`);
      } else {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/admin/upload/image", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (p) => { if (p.total) setProgress(Math.round((p.loaded / p.total) * 100)); },
          timeout: 0,
        });
        onUploaded(data);
        toast.success(`${label} uploaded`);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-white/50">{label}</label>
      <div className="mt-1.5 flex items-center gap-3">
        <input ref={ref} type="file" accept={accept} onChange={handle} className="hidden" data-testid={testid} />
        <button type="button" onClick={() => ref.current?.click()} disabled={busy}
          className="inline-flex items-center gap-2 rounded-lg bg-white/10 border border-white/20 px-4 py-2.5 text-sm text-white hover:bg-white/20 transition-colors disabled:opacity-60">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <UploadCloud size={16} />}
          {busy ? `Uploading ${progress}%` : `Choose ${label}`}
        </button>
        {value && !busy && <span className="flex items-center gap-1 text-xs text-[#00E676]"><CheckCircle2 size={14} /> Ready</span>}
      </div>
      {busy && (
        <div className="mt-2 h-1.5 w-full rounded-full bg-white/10 overflow-hidden">
          <div className="h-full bg-[#00F0FF] transition-all" style={{ width: `${progress}%` }} />
        </div>
      )}
    </div>
  );
};

const Admin = () => {
  const [movies, setMovies] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/movies").then((r) => setMovies(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(EMPTY); setEditing(null); setOpen(true); };
  const openEdit = (m) => {
    const { id, created_at, ...rest } = m;
    setForm({ ...EMPTY, ...rest }); setEditing(id); setOpen(true);
  };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    if (!form.stream_url) { toast.error("Upload a video or paste a stream URL"); return; }
    if (!form.poster_url) { toast.error("Upload a poster or paste a poster URL"); return; }
    setSaving(true);
    const payload = {
      ...form,
      backdrop_url: form.backdrop_url || form.poster_url,
      release_year: Number(form.release_year),
      rating: Number(form.rating),
    };
    try {
      if (editing) { await api.put(`/movies/${editing}`, payload); toast.success("Film updated"); }
      else { await api.post("/movies", payload); toast.success("Film published"); }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this film?")) return;
    try { await api.delete(`/movies/${id}`); toast.success("Film deleted"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="min-h-screen bg-[#030303] pt-24 pb-20 px-4 sm:px-8 max-w-[1600px] mx-auto">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <div>
          <span className="text-xs uppercase tracking-[0.3em] text-[#00F0FF]">Owner Studio</span>
          <h1 className="font-heading font-extrabold text-3xl sm:text-4xl tracking-tighter text-white mt-1">Admin Dashboard</h1>
          <p className="text-white/50 text-sm mt-1">{movies.length} films in the Power2000 library</p>
        </div>
        <button onClick={openNew} data-testid="admin-add-btn" className="inline-flex items-center gap-2 rounded-full bg-[#00F0FF] text-black font-semibold px-5 py-2.5 hover:scale-105 transition-transform">
          <Plus size={18} /> Upload Film
        </button>
      </div>

      <div className="grid gap-3">
        {movies.map((m) => (
          <div key={m.id} className="flex items-center gap-4 bg-[#0d0d0d] border border-white/10 rounded-xl p-3" data-testid={`admin-row-${m.id}`}>
            <img src={m.poster_url} alt={m.title} className="w-12 h-16 object-cover rounded shrink-0 bg-[#1a1a1a]" />
            <div className="flex-1 min-w-0">
              <h3 className="font-heading font-medium text-white truncate">{m.title}</h3>
              <p className="text-xs text-white/50">{m.category} · {m.release_year} · {m.quality} · {String(m.stream_url).startsWith("/api/stream/") ? "UPLOADED" : (m.stream_type || "mp4").toUpperCase()}</p>
            </div>
            <button onClick={() => openEdit(m)} data-testid={`admin-edit-${m.id}`} className="p-2 text-white/70 hover:text-[#00F0FF]"><Pencil size={18} /></button>
            <button onClick={() => remove(m.id)} data-testid={`admin-delete-${m.id}`} className="p-2 text-white/70 hover:text-[#FF2A2A]"><Trash2 size={18} /></button>
          </div>
        ))}
      </div>

      {open && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={() => setOpen(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={save} className="w-full max-w-2xl max-h-[88vh] overflow-y-auto bg-[#0d0d0d] border border-white/10 rounded-2xl p-6" data-testid="admin-form">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading font-bold text-xl text-white flex items-center gap-2"><Film size={20} className="text-[#00F0FF]" /> {editing ? "Edit" : "Upload"} Film</h2>
              <button type="button" onClick={() => setOpen(false)} className="text-white/60 hover:text-white"><X size={22} /></button>
            </div>

            {/* Upload studio */}
            <div className="grid grid-cols-1 gap-5 mb-6 p-4 rounded-xl border border-white/10 bg-black/30">
              <Uploader label="Video File" kind="video" accept="video/*" value={form.stream_url}
                testid="upload-video"
                onUploaded={(d) => setForm((f) => ({ ...f, stream_url: d.url, stream_type: d.stream_type || "mp4" }))} />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Uploader label="Poster Image" kind="image" accept="image/*" value={form.poster_url}
                  testid="upload-poster"
                  onUploaded={(d) => setForm((f) => ({ ...f, poster_url: d.url }))} />
                <Uploader label="Backdrop Image" kind="image" accept="image/*" value={form.backdrop_url}
                  testid="upload-backdrop"
                  onUploaded={(d) => setForm((f) => ({ ...f, backdrop_url: d.url }))} />
              </div>
              <p className="text-xs text-white/40">Large feature films (2h+) upload directly to secure cloud storage and stream with seek support. You can also paste external URLs below.</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2"><Field label="Title"><input required value={form.title} onChange={(e) => set("title", e.target.value)} className={inputCls} data-testid="admin-field-title" /></Field></div>
              <div className="sm:col-span-2"><Field label="Description"><textarea required rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} className={inputCls} /></Field></div>
              <Field label="Release Year"><input type="number" value={form.release_year} onChange={(e) => set("release_year", e.target.value)} className={inputCls} /></Field>
              <Field label="Origin"><input value={form.origin} onChange={(e) => set("origin", e.target.value)} className={inputCls} /></Field>
              <Field label="Quality">
                <select value={form.quality} onChange={(e) => set("quality", e.target.value)} className={inputCls}>
                  {["SD", "HD", "4K", "HDR"].map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </Field>
              <Field label="Genre"><input value={form.genre} onChange={(e) => set("genre", e.target.value)} className={inputCls} /></Field>
              <Field label="Category"><input value={form.category} onChange={(e) => set("category", e.target.value)} className={inputCls} /></Field>
              <Field label="Duration"><input value={form.duration} onChange={(e) => set("duration", e.target.value)} className={inputCls} placeholder="01:45:00" /></Field>
              <Field label="Rating"><input type="number" step="0.1" value={form.rating} onChange={(e) => set("rating", e.target.value)} className={inputCls} /></Field>
              <div className="sm:col-span-2"><Field label="Stream URL (auto-filled on upload)"><input required value={form.stream_url} onChange={(e) => set("stream_url", e.target.value)} className={inputCls} data-testid="admin-field-stream" placeholder="/api/stream/... or external URL" /></Field></div>
              <div className="sm:col-span-2"><Field label="Poster URL (auto-filled on upload)"><input required value={form.poster_url} onChange={(e) => set("poster_url", e.target.value)} className={inputCls} placeholder="/api/files/... or external URL" /></Field></div>
              <label className="sm:col-span-2 flex items-center gap-2 text-white/70 text-sm cursor-pointer">
                <input type="checkbox" checked={form.featured} onChange={(e) => set("featured", e.target.checked)} className="accent-[#00F0FF] w-4 h-4" /> Feature on homepage hero
              </label>
            </div>

            <button type="submit" disabled={saving} data-testid="admin-save-btn" className="mt-6 w-full rounded-full bg-[#00F0FF] text-black font-semibold py-3 hover:scale-[1.01] transition-transform disabled:opacity-60">
              {saving ? "Saving..." : editing ? "Update Film" : "Publish Film"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

export default Admin;
